import styled from '@emotion/styled';
import { useCallback, useState, type FC } from 'react';

import { Button } from 'src/components/common/atoms';
import {
  Content as CommonContent,
  type Props as CommonContentProps,
} from 'src/components/common/entry/common/content';
import { RichText } from 'src/components/common/rich-text';
import type { Props as RichTextProps } from 'src/components/common/rich-text/rich-text-renderer';
import { ScreenReaderTextStyle } from 'src/components/common/screen-reader-text';

export type Props = CommonContentProps & {
  className?: string;
  content?: RichTextProps['document'] | null;
  isDefaultHideContent?: boolean;
};

type UseIsHideContentProps = Pick<
  Props,
  'image' | 'meta' | 'isDefaultHideContent'
>;
const useIsHideContent = ({
  image,
  meta,
  isDefaultHideContent,
}: UseIsHideContentProps) => {
  const defaultIsShown =
    isDefaultHideContent === undefined
      ? !!image || !!meta
      : isDefaultHideContent;
  const [isHideContent, setIsHideContent] = useState(defaultIsShown);
  const showContent = useCallback(() => {
    setIsHideContent(false);
  }, []);
  return { isHideContent, showContent };
};

export const Content: FC<Props> = ({
  className,
  imageAlt,
  image,
  meta,
  content,
  isDefaultHideContent,
}) => {
  const { isHideContent, showContent } = useIsHideContent({
    image,
    meta,
    isDefaultHideContent,
  });
  return (
    <CommonContent
      className={className ?? ''}
      image={image}
      imageAlt={imageAlt}
      meta={meta}
    >
      {content?.raw && (
        <>
          {isHideContent && (
            <StyledButton aria-hidden={true} onClick={showContent}>
              more info
            </StyledButton>
          )}
          <StyledRichText
            className={isHideContent ? 'hide' : ''}
            document={content}
            isArchivePage={true}
          />
        </>
      )}
    </CommonContent>
  );
};

const StyledButton = styled(Button)`
  margin-top: 2em;
  cursor: pointer;
  &:hover,
  &:focus-visible {
    text-decoration: underline;
  }
`;

const StyledRichText = styled(RichText)`
  &.hide {
    ${ScreenReaderTextStyle}
  }
`;

export default Content;
